/**
 * Follow-Up Service for secure follow-up questionnaire access.
 * 
 * This service handles:
 * - Token verification against backend
 * - Access token management
 * - Prefill data retrieval
 * - Session status tracking
 * 
 * SECURITY: This service is the frontend counterpart to the backend
 * FollowUpService. It enforces the access gate flow:
 * 1. Verify token → Get auth requirement
 * 2. If AUTH_REQUIRED → Redirect to login
 * 3. If OK → Store access token, load questionnaire
 */
import config from '../utils/config';
import {
  API_PATH,
  joinApiEndpoint,
  joinApiV1Endpoint,
  normalizeApiV1Base,
} from '../constants/apiEndpoints';

class FollowUpService {
  constructor() {
    this.accessToken = null;
    this.accessTokenExpiry = null;
    this.sessionId = null;
    this.questionnaireId = null;
    this.REQUEST_TIMEOUT_MS = 20000;
  }

  normalizeApiBase(apiBase) {
    if (!apiBase) {
      return null;
    }

    const trimmed = String(apiBase).trim();
    if (!trimmed) {
      return null;
    }

    return normalizeApiV1Base(trimmed);
  }

  getApiBaseCandidates() {
    const candidates = [];
    const seen = new Set();

    const pushCandidate = (value) => {
      const normalized = this.normalizeApiBase(value);
      if (!normalized || seen.has(normalized)) {
        return;
      }
      seen.add(normalized);
      candidates.push(normalized);
    };

    pushCandidate(localStorage.getItem('welliemd_client_api_endpoint'));
    pushCandidate(config.API_BASE_URL);

    // Backward-compatible local dev fallback for older portal links that do not
    // yet include api_base_url in the questionnaire URL.
    if (
      typeof window !== 'undefined' &&
      ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ) {
      pushCandidate('http://localhost:8001');
      pushCandidate('http://127.0.0.1:8001');
      pushCandidate('http://localhost:8101');
      pushCandidate('http://127.0.0.1:8101');
      pushCandidate('http://localhost:8000');
      pushCandidate('http://127.0.0.1:8000');
    }

    return candidates;
  }

  createAbortController() {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => {
      controller.abort();
    }, this.REQUEST_TIMEOUT_MS);

    return {
      controller,
      cleanup: () => window.clearTimeout(timeoutId),
    };
  }

  /**
   * Verify a follow-up token and determine next steps.
   * 
   * @param {string} sessionId - UUID of the follow-up session
   * @param {string} token - Raw token from URL
   * @returns {Promise<Object>} Verification result with status and next steps
   */
  async verifyToken(sessionId, token) {
    try {
      console.log('🔐 Verifying follow-up token:', { sessionId });
      
      const clientApiEndpoint = localStorage.getItem('welliemd_client_api_endpoint');
      const apiToken = process.env.REACT_APP_API_TOKEN || '';
      
      // Client API endpoint doesn't include /api/v1, but config.API_BASE_URL does
      // Normalize to always use /api/v1 path
      let apiUrl;
      if (clientApiEndpoint) {
        apiUrl = joinApiEndpoint(clientApiEndpoint, API_PATH.followUpVerify);
      } else {
        // config.API_BASE_URL already has /api/v1
        apiUrl = joinApiV1Endpoint(config.API_BASE_URL, API_PATH.followUpVerify);
      }
      
      // Verify endpoint is public (AllowAny) - no authentication required
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          token: token
        })

      });

      const data = await response.json();
      
      console.log('🔐 Verification result:', { status: data.status });

      // Store session info for later use
      if (data.status === 'OK') {
        this.accessToken = data.access_token;
        this.accessTokenExpiry = Date.now() + (data.access_token_expires_in * 1000);
        this.sessionId = data.session_id;
        this.questionnaireId = data.questionnaire_id;
        
        // Store in sessionStorage (not localStorage - more secure)
        sessionStorage.setItem('followup_access_token', data.access_token);
        sessionStorage.setItem('followup_session_id', data.session_id);
        sessionStorage.setItem('followup_questionnaire_id', data.questionnaire_id);
      }

      return data;
      
    } catch (error) {
      console.error('❌ Follow-up verification failed:', error);
      return {
        status: 'FORBIDDEN',
        error_code: 'NETWORK_ERROR',
        session_id: sessionId,
        questionnaire_id: null,
        prefill_available: false,
        login_redirect_url: null,
        access_token: null,
        access_token_expires_in: null
      };
    }
  }

  /**
   * Get session info for pre-authenticated flow.
   * Used to get questionnaire ID when accessing from Patient Portal.
   * 
   * @param {string} sessionId - Session ID from URL
   * @returns {Promise<Object>} Session info including questionnaire_id
   */
  async getSessionInfo(sessionId) {
    try {
      const clientApiEndpoint = localStorage.getItem('welliemd_client_api_endpoint');
      const accessToken = this.getAccessToken();
      
      if (!accessToken) {
        throw new Error('No access token available');
      }
      
      let apiUrl;
      if (clientApiEndpoint) {
        apiUrl = joinApiEndpoint(clientApiEndpoint, API_PATH.followUpInfo(sessionId));
      } else {
        // FollowUpSession lives in Admin DB, so use Admin backend
        apiUrl = joinApiV1Endpoint(config.API_BASE_URL, API_PATH.followUpInfo(sessionId));
      }
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-FollowUp-Access-Token': accessToken
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get session info: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Store questionnaire ID
      if (data.questionnaire_id) {
        this.questionnaireId = data.questionnaire_id;
        sessionStorage.setItem('followup_questionnaire_id', data.questionnaire_id);
      }
      
      return data;
      
    } catch (error) {
      console.error('❌ Failed to get session info:', error);
      throw error;
    }
  }


  /**
   * Start the follow-up questionnaire and get prefill data.
   * 
   * @returns {Promise<Object>} Start result with prefill data
   */
  async startQuestionnaire() {
    const accessToken = this.getAccessToken();
    
    if (!accessToken) {
      throw new Error('No access token available. Please verify token first.');
    }

    try {
      const debugEnabled =
        typeof window !== "undefined" &&
        new URLSearchParams(window.location.search).get("debug") === "1";
      const apiBaseCandidates = this.getApiBaseCandidates();
      let lastError = null;

      for (const apiBase of apiBaseCandidates) {
        let apiUrl = `${apiBase}/questionnaires/follow-ups/start/`;
        if (debugEnabled) {
          apiUrl = `${apiUrl}?debug=1`;
        }

        const { controller, cleanup } = this.createAbortController();
        try {
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-FollowUp-Access-Token': accessToken
            },
            signal: controller.signal,
            body: JSON.stringify({
              session_id: this.sessionId
            })
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const message = errorData.error || `Start questionnaire failed: ${response.status}`;
            lastError = new Error(message);
            console.warn(`⚠️ Follow-up start failed via ${apiBase}:`, message);
            continue;
          }

          const data = await response.json();
          console.log('✅ Follow-up questionnaire started:', { apiBase, data });

          // Persist whichever endpoint actually worked so later follow-up calls
          // (prefill refresh, submit, info) stay on the correct tenant backend.
          localStorage.setItem('welliemd_client_api_endpoint', apiBase.replace(/\/api\/v1$/, ''));
          return data;
        } catch (error) {
          lastError = error;
          console.warn(`⚠️ Follow-up start network failure via ${apiBase}:`, error?.message || error);
        } finally {
          cleanup();
        }
      }

      throw lastError || new Error('Failed to initialize follow-up questionnaire.');
      
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw new Error('Follow-up initialization timed out. Please try again.');
      }
      console.error('❌ Failed to start follow-up questionnaire:', error);
      throw error;
    }
  }

  /**
   * Get the current access token if valid.
   * 
   * @returns {string|null} Access token or null if expired/missing
   */
  getAccessToken() {
    // Check memory cache first
    if (this.accessToken && this.accessTokenExpiry && Date.now() < this.accessTokenExpiry) {
      return this.accessToken;
    }
    
    // Fall back to sessionStorage
    const storedToken = sessionStorage.getItem('followup_access_token');
    if (storedToken) {
      this.accessToken = storedToken;
      return storedToken;
    }
    
    return null;
  }

  /**
   * Check if we have a valid follow-up context.
   * 
   * @returns {boolean} True if in valid follow-up context
   */
  isInFollowUpContext() {
    return !!this.getAccessToken() && !!this.sessionId;
  }

  /**
   * Get the access token header for API calls.
   * Use this when making questionnaire API calls in follow-up context.
   * 
   * @returns {Object} Headers object with access token
   */
  getAccessTokenHeader() {
    const token = this.getAccessToken();
    return token ? { 'X-FollowUp-Access-Token': token } : {};
  }

  /**
   * Store access token from URL when patient is pre-authenticated from Patient Portal.
   * This bypasses the verify step since patient ownership was already verified.
   * 
   * @param {string} sessionId - Session ID from URL
   * @param {string} accessToken - Access token from URL
   * @param {string} questionnaireId - Questionnaire ID (if known)
   */
  storeAccessTokenFromPortal(sessionId, accessToken, questionnaireId = null) {
    console.log('🔓 Storing pre-authenticated access token from Patient Portal');
    
    this.accessToken = accessToken;
    // Set expiry to 1 hour from now (conservative estimate)
    this.accessTokenExpiry = Date.now() + (3600 * 1000);
    this.sessionId = sessionId;
    this.questionnaireId = questionnaireId;
    
    // Store in sessionStorage
    sessionStorage.setItem('followup_access_token', accessToken);
    sessionStorage.setItem('followup_session_id', sessionId);
    if (questionnaireId) {
      sessionStorage.setItem('followup_questionnaire_id', questionnaireId);
    }
  }


  /**
   * Clear follow-up context (on completion or error).
   */
  clearContext() {
    this.accessToken = null;
    this.accessTokenExpiry = null;
    this.sessionId = null;
    this.questionnaireId = null;
    
    sessionStorage.removeItem('followup_access_token');
    sessionStorage.removeItem('followup_session_id');
    sessionStorage.removeItem('followup_questionnaire_id');
    
    console.log('🧹 Follow-up context cleared');
  }

  /**
   * Parse follow-up parameters from URL.
   * Expected format:
   * /follow-up/:sessionId?token=xxx[&access_token=yyy][&client_id=zzz][&api_base_url=...]
   * 
   * @returns {Object|null} { sessionId, token, accessToken, clientId, apiBaseUrl } or null if not a follow-up URL
   */
  static parseFollowUpUrl() {
    const pathParts = window.location.pathname.split('/').filter(part => part);
    const params = new URLSearchParams(window.location.search);
    
    // Check if this is a follow-up or onboarding URL
    const followUpIndex = pathParts.indexOf('follow-up');
    const onboardingIndex = pathParts.indexOf('onboarding');
    
    if (followUpIndex === -1 && onboardingIndex === -1) {
      return null;
    }
    
    const activeIndex = followUpIndex !== -1 ? followUpIndex : onboardingIndex;
    const sessionId = pathParts[activeIndex + 1];
    const token = params.get('token');
    const accessToken = params.get('access_token');
    const clientId = params.get('client_id') || params.get('clientId');
    const apiBaseUrl =
      params.get('api_base_url') ||
      params.get('apiBaseUrl') ||
      params.get('client_api_endpoint');
    
    if (!sessionId || !token) {
      console.warn('Invalid follow-up URL: missing sessionId or token');
      return null;
    }
    
    // Validate UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(sessionId)) {
      console.warn('Invalid follow-up URL: sessionId is not a valid UUID');
      return null;
    }
    
    // If accessToken is present, patient is pre-authenticated from Patient Portal
    if (accessToken) {
      console.log('🔓 Pre-authenticated follow-up URL detected (from Patient Portal)');
    }
    
    return { sessionId, token, accessToken, clientId, apiBaseUrl };
  }

}

// Export singleton instance
const followUpService = new FollowUpService();
export default followUpService;

// Also export class for testing
export { FollowUpService };
